import { API_CONFIG } from '../config/api';
import { clerkService } from './clerkService';

export class FigureService {
    constructor() {
        this.cache = new Map();
        this.cacheTimestamps = new Map();
    }
    
    // Helper to get auth headers
    async _getAuthHeaders() {
        const token = await clerkService.getToken();
        const headers = {};
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }
        return headers;
    }

    // Helper to fetch image with auth and return as data URL
    async fetchImageAsDataUrl(imageUrl) {
        try {
            const headers = await this._getAuthHeaders();
            const fullUrl = imageUrl.startsWith('http') 
                ? imageUrl 
                : `${API_CONFIG.PATDRAW_API_URL}${imageUrl}`;
            
            const response = await fetch(fullUrl, { headers });
            
            if (!response.ok) {
                console.error('Failed to fetch image:', response.status, response.statusText);
                return null;
            }
            
            const blob = await response.blob();
            return new Promise((resolve) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result);
                reader.readAsDataURL(blob);
            });
        } catch (error) {
            console.error('Error fetching image:', error);
            return null;
        }
    }

    // Cache management
    _getCached(key) {
        const timestamp = this.cacheTimestamps.get(key);
        if (timestamp && Date.now() - timestamp < API_CONFIG.CACHE.FIGURES_TTL) {
            return this.cache.get(key);
        }
        return null;
    }

    _setCached(key, data) {
        this.cache.set(key, data);
        this.cacheTimestamps.set(key, Date.now());
    }

    clearCache() {
        this.cache.clear();
        this.cacheTimestamps.clear();
        // console.log('Figure cache cleared');
    }

    // API methods
    async fetchFigures() {
        try {
            // Check cache first
            const cached = this._getCached('figures');
            if (cached) {
                return cached;
            }

            const headers = await this._getAuthHeaders();
            
            // Add timeout to prevent infinite loading
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
            
            try {
                const response = await fetch(`${API_CONFIG.PATDRAW_API_URL}${API_CONFIG.ENDPOINTS.FIGURES}`, {
                    headers,
                    signal: controller.signal
                });
                clearTimeout(timeoutId);
                
                if (!response.ok) {
                    console.error(`Figures API error: ${response.status} ${response.statusText}`);
                    if (response.status === 401) {
                        console.error('Authentication failed - check if Patdraw backend has CLERK_SECRET_KEY configured');
                    }
                    throw new Error(`Failed to fetch figures: ${response.statusText}`);
                }
                
                const data = await response.json();
                const figures = data.figures || [];
                
                // Fetch preview images and convert to data URLs for authenticated access
                const figuresWithImages = await Promise.all(
                    figures.map(async (figure) => {
                        if (figure.previewUrl && !figure.imageData) {
                            const imageData = await this.fetchImageAsDataUrl(figure.previewUrl);
                            return { ...figure, imageData };
                        }
                        return figure;
                    })
                );
                
                // Cache the result
                this._setCached('figures', figuresWithImages);
                
                return figuresWithImages;
            } catch (fetchError) {
                clearTimeout(timeoutId);
                if (fetchError.name === 'AbortError') {
                    console.error('Fetch figures timed out after 10 seconds');
                    throw new Error('Request timed out');
                }
                throw fetchError;
            }
        } catch (error) {
            console.error('Error fetching figures:', error);
            // Return cached data if available, even if expired
            const staleCache = this.cache.get('figures');
            if (staleCache) {
                console.log('Returning stale cache due to error');
                return staleCache;
            }
            return [];
        }
    }

    async fetchNumerals() {
        try {
            // Check cache first
            const cached = this._getCached('numerals');
            if (cached) {
                return cached;
            }

            const headers = await this._getAuthHeaders();
            
            // Add timeout to prevent infinite loading
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
            
            try {
                const response = await fetch(`${API_CONFIG.PATDRAW_API_URL}/api/numerals`, {
                    headers,
                    signal: controller.signal
                });
                clearTimeout(timeoutId);
                
                if (!response.ok) {
                    console.error(`Numerals API error: ${response.status} ${response.statusText}`);
                    if (response.status === 401) {
                        console.error('Authentication failed - check if Patdraw backend has CLERK_SECRET_KEY configured');
                    }
                    throw new Error(`Failed to fetch numerals: ${response.statusText}`);
                }
                
                const data = await response.json();
                const numerals = data.numerals || [];
                
                // Cache the result  
                this._setCached('numerals', numerals);
                
                return numerals;
            } catch (fetchError) {
                clearTimeout(timeoutId);
                if (fetchError.name === 'AbortError') {
                    console.error('Fetch numerals timed out after 10 seconds');
                    throw new Error('Request timed out');
                }
                throw fetchError;
            }
        } catch (error) {
            console.error('Error fetching numerals:', error);
            // Return cached data if available, even if expired
            const staleCache = this.cache.get('numerals');
            if (staleCache) {
                console.log('Returning stale cache due to error');
                return staleCache;
            }
            return [];
        }
    }

    async getFigure(figureId) {
        try {
            // Check cache first
            const cacheKey = `figure_${figureId}`;
            const cached = this._getCached(cacheKey);
            if (cached) return cached;

            const headers = await this._getAuthHeaders();
            const response = await fetch(
                `${API_CONFIG.PATDRAW_API_URL}${API_CONFIG.ENDPOINTS.FIGURE_BY_ID(figureId)}`,
                { headers }
            );
            
            if (!response.ok) {
                if (response.status === 404) return null;
                throw new Error(`Failed to fetch figure: ${response.statusText}`);
            }
            
            const figure = await response.json();
            
            // Cache the result
            this._setCached(cacheKey, figure);
            
            return figure;
        } catch (error) {
            console.error('Error fetching figure:', error);
            return null;
        }
    }

    async getFigureImageUrl(figureId, size = 'full') {
        // Return the URL for the figure image
        // The actual image will be fetched by the browser
        return `${API_CONFIG.PATDRAW_API_URL}${API_CONFIG.ENDPOINTS.FIGURE_IMAGE(figureId, size)}`;
    }

    async getFigureMenuItems() {
        try {
            const figures = await this.fetchFigures();
            const menuItems = figures.map(fig => {
                return {
                    id: fig.id,
                    name: fig.name || 'Untitled Figure',
                    imageData: fig.imageData, // Use the data URL that was fetched with auth
                    previewUrl: fig.previewUrl, // Keep original URL as fallback
                    type: 'figure'
                };
            });
            
            // Try to get numerals, but don't fail if they're unavailable
            let numeralItems = [];
            try {
                const numerals = await this.fetchNumerals();
                
                // Create a map of figureId to figure index for calculating X0Y format
                const figureIdToIndex = new Map();
                figures.forEach((figure, index) => {
                    figureIdToIndex.set(figure.id, index + 1); // 1-based indexing
                });
                
                // Filter out cross-references - only include source numerals in the menu
                // Cross-references have isCrossReference: true
                const sourceNumerals = numerals.filter(numeral => !numeral.isCrossReference);
                
                numeralItems = await Promise.all(sourceNumerals.map(async numeral => {
                    // Use the figure's preview image for the numeral thumbnail, fetch with auth
                    let figureImageData = null;
                    if (numeral.figureId) {
                        try {
                            const figurePreviewUrl = `/api/figures/${numeral.figureId}/image?size=preview`;
                            figureImageData = await this.fetchImageAsDataUrl(figurePreviewUrl);
                        } catch (imgError) {
                            console.warn('Failed to fetch numeral preview image:', imgError);
                        }
                    }
                    
                    // Calculate X0Y format where X is figure number and Y is numeral value
                    let displayValue = numeral.value;
                    if (numeral.figureId && figureIdToIndex.has(numeral.figureId)) {
                        const figureNumber = figureIdToIndex.get(numeral.figureId);
                        const paddedNumeralValue = String(numeral.value).padStart(2, '0');
                        displayValue = parseInt(`${figureNumber}${paddedNumeralValue}`);
                    }
                    
                    return {
                        id: numeral.id,
                        name: numeral.name || `numeral_${numeral.value}`,
                        displayName: numeral.name || `Numeral ${displayValue}`,
                        imageData: figureImageData, // Use authenticated data URL
                        type: 'numeral',
                        figureId: numeral.figureId,
                        figureName: numeral.figureName,
                        value: displayValue, // Use calculated X0Y format
                        reference: numeral.reference
                    };
                }));
            } catch (numeralError) {
                console.warn('Failed to fetch numerals for menu, continuing with figures only:', numeralError);
                // Continue without numerals - figures are more important
            }
            
            // Combine figures and numerals - both appear in the same dropdown
            const allItems = [...menuItems, ...numeralItems];
            
            // Sort alphabetically by name
            return allItems.sort((a, b) => {
                const nameA = a.displayName || a.name;
                const nameB = b.displayName || b.name;
                return nameA.localeCompare(nameB, undefined, { numeric: true, sensitivity: 'base' });
            });
        } catch (error) {
            console.error('Failed to get figure menu items:', error);
            // Return empty array rather than throwing
            return [];
        }
    }

    async getFigureByKey(key) {
        try {
            const figure = await this.getFigure(key);
            if (figure && figure.previewUrl) {
                // Add imageData property for FigurePreview component with cache-busting
                const separator = figure.previewUrl.includes('?') ? '&' : '?';
                figure.imageData = `${API_CONFIG.PATDRAW_API_URL}${figure.previewUrl}${separator}t=${Date.now()}`;
            }
            return figure;
        } catch (error) {
            // Return null for 404 errors (figure deleted in Patdraw)
            if (error.message && (error.message.includes('404') || error.message.includes('Not Found'))) {
                return null;
            }
            throw error;
        }
    }

    getFigureNumber(figureKey, content) {
        const references = this.extractFigureReferences(content);
        const position = references.indexOf(figureKey) + 1;
        return position > 0 ? position : 0;
    }

    extractFigureReferences(content) {
        const references = [];
        const regex = /data-figure-key="([^"]+)"/g;
        let match;
        
        while ((match = regex.exec(content)) !== null) {
            if (!references.includes(match[1])) {
                references.push(match[1]);
            }
        }
        
        return references;
    }

    async validateFigureKey(key) {
        try {
            // Check if it's a prefixed compound key for numerals (format: numeral:figureId:numeralId)
            if (key.startsWith('numeral:')) {
                const parts = key.split(':');
                if (parts.length === 3) {
                    const [_prefix, figureId, numeralId] = parts;
                    
                    try {
                        const numerals = await this.fetchNumerals();
                        const isValidNumeral = numerals.some(n => 
                            n.figureId === figureId && n.id === numeralId
                        );
                        if (isValidNumeral) {
                            return true; // Valid numeral reference
                        }
                        // If numeral not found but fetch succeeded, it's truly invalid
                        console.log(`[FigureService] Invalid numeral reference ${key}`);
                        return false;
                    } catch (error) {
                        // Network/auth failure - assume valid to prevent false negatives
                        console.warn(`[FigureService] Cannot validate numeral ${key} due to fetch error, assuming valid:`, error.message);
                        return true;
                    }
                }
            }
            
            // Check if it's a table reference (format: table:tableId)
            if (key.startsWith('table:')) {
                // Assume all table references are valid since we don't have a table service
                return true;
            }
            
            // Check if it's a figure by name or ID
            try {
                const figures = await this.fetchFigures();
                const figureByName = figures.find(f => f.name === key);
                if (figureByName) {
                    return true; // Valid figure reference by name
                }
                
                // Also check by ID
                const figureById = figures.find(f => f.id === key);
                if (figureById) {
                    return true; // Valid figure reference by ID
                }
                
                // If fetch succeeded but figure not found, it's truly invalid
                console.log(`[FigureService] Figure ${key} is invalid (deleted or not found)`);
                return false;
            } catch (error) {
                // Network/auth failure - assume valid to prevent false negatives
                console.warn(`[FigureService] Cannot validate figure ${key} due to fetch error, assuming valid:`, error.message);
                return true;
            }
        } catch (error) {
            // Catch-all - assume valid on any unexpected error
            console.error(`[FigureService] Unexpected error validating ${key}, assuming valid:`, error);
            return true;
        }
    }

    async getFigureReferenceText(figureKey) {
        const figure = await this.getFigureByKey(figureKey);
        if (!figure) return '@{unknown}';
        return figure.name ? `@{figure:${figure.name}}` : '@{figure:Unnamed Figure}';
    }
    
    getFigureTypeLabel(_type) {
        return 'Figure';
    }
    
    async getFullFigureLabel(figureKey, content) {
        const figure = await this.getFigureByKey(figureKey);
        if (!figure) return 'Unknown Figure';
        
        const figureNumber = this.getFigureNumber(figureKey, content);
        const typeLabel = this.getFigureTypeLabel(figure.type);
        
        return `${typeLabel} ${figureNumber}: ${figure.name}`;
    }

    // New method to sync figure order with Patdraw
    async updateFigureOrder(orderedFigureIds, figureNumbers) {
        try {
            const authHeaders = await this._getAuthHeaders();
            const response = await fetch(
                `${API_CONFIG.PATDRAW_API_URL}${API_CONFIG.ENDPOINTS.SHEET_ORDER}`,
                {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        ...authHeaders
                    },
                    body: JSON.stringify({
                        orderedFigureIds,
                        figureNumbers
                    })
                }
            );
            
            if (!response.ok) {
                throw new Error(`Failed to update figure order: ${response.statusText}`);
            }
            
            // Clear cache after order update
            this.clearCache();
            
            return await response.json();
        } catch (error) {
            console.error('Error updating figure order:', error);
            return { success: false, error: error.message };
        }
    }
}

export const figureService = new FigureService();
